// The full-page checkout in steps (owner, 25 Sept: "Full-page checkout
// switches on by itself for long forms", and "one card on screen at a time on
// mobile"). CartPanel opens it instead of the 420px drawer when
// checkoutLayout says "page". It covers the widget's viewport and walks the
// guest through Your details → {Riders} → Booking details → Review & pay;
// a step with nothing in it is left out.
//
// The money path is the drawer's: useCheckoutSubmit() (codes, gift cards,
// the ChiDesk mixed cart, deferred deposits, Paystack, the commit order) and
// the same CheckoutParts. Each rider is checked HERE and SAVED as the guest
// goes (the owner: "Surely we can automatically save as we progress?"): Next,
// Back or another rider's chip saves the rider on screen with the one-rider
// writer (save_booking_participant_row; state/rider-autosave.ts), so a closed
// tab or a lost signal loses at most the rider being typed. A partly filled
// rider is never saved and never ticked (I1). The end of the Riders step
// saves any complete rider still not in, one at a time (never the whole list
// again); the booking answers go with save_booking_group_details at the end
// of Booking details; Pay runs the hook's commit (any rider still not in, the
// booking answers still unsaved, then the statements) and the intent, in
// today's order, and the server's payment gate stays the judge. A reload or
// the trip to Paystack reopens Riders at the first rider not saved yet, the
// saved ones ticked from the server's reader. Pay exists only on Review.
import { useEffect, useId, useMemo, useRef, useState, type FormEvent, type MouseEvent as ReactMouseEvent } from "react";
import { resolveCheckoutPresentation, type CheckoutPresentation, type GuestNoun } from "@vertu/shared";
import type { WidgetTenant } from "../../types";
import type { CartItem } from "../../state/cart";
import { getCartToken } from "../../state/booking";
import { normalizePhoneE164 } from "../../lib/phone";
import { panelLineZone } from "../../state/tenant-time";
import { DEFAULT_NOUN, nounTitle, type CheckoutForm } from "../../state/booking-questions-form";
import {
  checkoutSteps,
  formatDue,
  noticeNow,
  summaryDiscount,
  nextStepOf,
  prevStepOf,
  stepOrFirst,
  stepPositionText,
  type ResumePayment,
  type StepId,
} from "../../state/checkout-steps";
import { ParticipantDetails, type ParticipantSummary, type SteppedForms } from "../ParticipantDetails";
import { HoldTimer } from "../HoldTimer";
import { useVenueIdentityOr, VenueMark } from "../VenueMark";
import { hopPayment, useCheckoutSubmit, isChideskItem, type CheckoutConfirmPayload } from "./useCheckoutSubmit";
import {
  CheckoutConsents,
  CheckoutTotals,
  CohostNotice,
  ContactFields,
  PayNotes,
  SpecialRequestsField,
  payBlocked,
  GuardNotice,
} from "./CheckoutParts";
import { CheckoutProgress } from "./CheckoutProgress";
import { SummaryAside, SummaryBar, type SummaryLine, type SummaryRider } from "./CheckoutSummary";
import { clearCheckoutResume, saveCheckoutResume, type ResumedCheckout } from "./useCheckoutResume";
import {
  leaveAfterSave,
  leaveBeforeSave,
  nothingLeftToSave,
  resumeRiderIndex,
  resumeSavedNotice,
  retryButton,
  ridersSkippedNotice,
  type LeaveOutcome,
  type RiderSaveProblem,
} from "../../state/rider-autosave";
import { LAPSE_MESSAGE } from "../../state/hold-timer-logic";
import { useFocusRequest } from "./useFocusRequest";

/**
 * Where the page puts the focus once the view it points at is on screen:
 *   heading  a step change: the step's heading, with "Step N of M" said
 *            (after `say`: "Rider 4 saved.", why the checkout reopened);
 *   title    another rider's card (Next, Back): its title;
 *   problem  an answer still missing: on Riders the card's question (else
 *            its title); on Booking details the booking's question (else
 *            back to `el`, the control pressed: a save the server refused);
 *   field    Your details: the field `id` with its message on screen (the
 *            message is drawn by the same update, so the field is focused
 *            and scrolled once it is there);
 *   restore  a save that did not move the guest on: back to `el` (the
 *            control pressed), if the focus was lost;
 *   message  the hold ended and the control is off: the words beside it.
 */
type PageFocus =
  | { to: "heading"; say: string | null }
  | { to: "title" }
  | { to: "problem"; el?: HTMLElement | null }
  | { to: "field"; id: string }
  | { to: "restore"; el: HTMLElement }
  | { to: "message" };

/** Focus `el` and say whether it has the focus now (a request is done only then). */
function take(el: HTMLElement | null | undefined): boolean {
  if (!el || !el.isConnected) return false;
  el.focus();
  return document.activeElement === el;
}

/**
 * A field that has the focus, with its message (aria-describedby, drawn
 * under it) on screen too: focusing scrolls only the field into view, and
 * the words under it could stay behind the action bar. The field wins
 * where both do not fit.
 */
function revealWithMessage(field: HTMLElement) {
  const ids = field.getAttribute("aria-describedby")?.split(/\s+/).filter(Boolean) ?? [];
  const words = ids.map((id) => document.getElementById(id)).filter((el): el is HTMLElement => !!el);
  const last = words[words.length - 1];
  if (!last) return;
  last.scrollIntoView({ block: "nearest" });
  field.scrollIntoView({ block: "nearest" });
}

/**
 * A press of Next or Back, never the second tap of a double tap (a click's
 * `detail` counts the taps; a key press is 0): the first tap has moved the
 * guest already, and the button under the finger now does something else
 * ("← Back" became "← Back to your visit"; Continue became Next on a card
 * the guest has not seen).
 */
function oneTap(act: () => Promise<void>) {
  return (e: ReactMouseEvent<HTMLButtonElement>) => {
    if (e.detail > 1) return;
    void act();
  };
}

/** The controls Tab reaches inside the page, in order (visible, enabled, not taken out of the order). */
function tabbable(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return [...root.querySelectorAll<HTMLElement>(selector)].filter(
    (el) => el.tabIndex >= 0 && el.getClientRects().length > 0 && !el.closest("[hidden], [inert]"),
  );
}

/** "Thu 1 Oct · 08:30" on the line's clock (the venue's for a panel line, else the browser's, as the drawer shows it). */
export function lineWhen(item: CartItem): string | null {
  const iso = item.metadata?.starts_at;
  if (typeof iso !== "string") return null;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return null;
  const timeZone = panelLineZone(item.metadata);
  try {
    const day = new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone }).format(ms);
    const hm = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(ms);
    return `${day} · ${hm}`;
  } catch {
    return null;
  }
}

function lineTitle(item: CartItem): string {
  if (isChideskItem(item)) {
    const treatment = item.metadata?.treatment_name;
    return typeof treatment === "string" && treatment ? treatment : item.description;
  }
  return item.unitName || item.serviceName || item.description;
}

function partyText(item: CartItem, noun: GuestNoun | null): string | null {
  const party = item.metadata?.party_size;
  const n = typeof party === "number" && party > 0 ? party : null;
  if (!n) return null;
  const words = noun ?? DEFAULT_NOUN;
  return `${n} ${n === 1 ? words.one : words.other}`;
}

export function CheckoutPage({
  tenant,
  items,
  currency,
  forms,
  phone,
  presentation,
  summaryImages,
  resume,
  onBack,
  onClose,
  onChooseAnotherTime,
  onConfirm,
}: {
  tenant: WidgetTenant;
  items: CartItem[];
  currency: string;
  /** The forms the layout was decided from: the steps are fixed from these. */
  forms: readonly CheckoutForm[];
  /** A container 640px or narrower: one card on screen, the bars top and bottom. */
  phone: boolean;
  /** Tenant-configured appearance; absent keeps the existing checkout. */
  presentation?: CheckoutPresentation;
  /** Images from the current widget catalogue, keyed by cart line id. */
  summaryImages?: Record<string, string>;
  /** A checkout restored after a reload or the Paystack round trip. */
  resume?: ResumedCheckout | null;
  /** "Back to your visit" (the cart). */
  onBack: () => void;
  onClose: () => void;
  /** After a refund with no booking: a fresh cart, back to the services (the page closes). */
  onChooseAnotherTime: () => void;
  onConfirm: (payload?: CheckoutConfirmPayload) => void;
}) {
  const appearance = resolveCheckoutPresentation(presentation);
  const welcome = appearance.layout === "welcome";
  const leave = (then: () => void) => () => {
    clearCheckoutResume(tenant.slug);
    then();
  };
  // The last trip to Paystack (a restored checkout carries its own): kept
  // in the resume entry so a reload asks the server about THIS payment
  // before it ever reopens Pay (useCheckoutReturn). Stamped only when the
  // browser actually leaves for Paystack, never on a Pay the checkout's own
  // checks or the server refused.
  const paymentRef = useRef<ResumePayment | null>(resume?.payment ?? null);
  // Read by onPaystackHop, which runs inside the payment call.
  const whereRef = useRef<{ step: StepId; riderIndex: number }>({ step: "details", riderIndex: 0 });
  const c = useCheckoutSubmit({
    tenant,
    items,
    currency,
    onConfirm: (payload) => {
      clearCheckoutResume(tenant.slug);
      onConfirm(payload);
    },
    onPaystackHop: (hop) => {
      paymentRef.current = hopPayment(hop);
      // Now, not in an effect: the page is about to be left.
      saveCheckoutResume(tenant.slug, {
        cartToken: getCartToken(),
        items,
        step: whereRef.current.step,
        riderIndex: whereRef.current.riderIndex,
        payment: paymentRef.current,
        codesApplied: hop.codesApplied,
        codeKinds: hop.codeKinds,
        linkCodeOnly: hop.linkCodeOnly,
        marketing: hop.marketing,
        given: hop.given,
      });
    },
    // Reopened after its trip to Paystack: what the guest gave, filled back in.
    reopened: resume?.fields ?? null,
  });

  // The steps, fixed from the definitions the page opened with: the progress
  // never grows or shrinks while the guest fills it in.
  const noun: GuestNoun = forms.find((f) => f.fields.length > 0)?.guest_noun ?? forms[0]?.guest_noun ?? DEFAULT_NOUN;
  const steps = useMemo(
    () =>
      checkoutSteps({
        riders: forms.some((f) => f.fields.length > 0),
        bookingDetails: forms.some((f) => f.group_fields.length > 0),
        noun,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const hasBookingStep = steps.some((s) => s.id === "booking");
  // A restored checkout reopens where the guest was. The booker's contact is
  // never kept with it (only the guest profile holds it, once they have
  // paid or signed in), so when it is not all there the page starts at Your
  // details, and Riders still reopens on the rider they were on.
  const [step, setStep] = useState<StepId>(() => {
    const contactReady =
      !!c.firstName.trim() && !!c.lastName.trim() && !!c.email.trim() && normalizePhoneE164(c.phone).ok;
    return contactReady ? stepOrFirst(steps, resume?.step) : "details";
  });
  const [riderIndex, setRiderIndex] = useState(() => (resume?.step === "riders" ? resume.riderIndex : 0));
  // What Next found missing on the card on screen (shown in the card too).
  const [riderMessage, setRiderMessage] = useState<string | null>(null);
  // Why the step could not move on, beside the primary button (role=alert).
  const [stepMessage, setStepMessage] = useState<string | null>(null);
  const [summary, setSummary] = useState<ParticipantSummary | null>(null);
  const [notice, setNotice] = useState<string | null>(resume?.notice ?? null);
  // A restored checkout opens on Your details (the contact is never kept):
  // until the guest moves on, say that the riders already in are safe.
  const [resumeNoteOn, setResumeNoteOn] = useState(!!resume);
  const [busy, setBusy] = useState(false);
  // Every press of the primary button or Back: the phone's summary folds
  // away on each, so an open summary never keeps the room the card needs.
  const [moves, setMoves] = useState(0);
  whereRef.current = { step, riderIndex };
  const riders = summary?.riders ?? [];
  // The page itself: the hold timer counts typing and tapping anywhere in it.
  const pageRef = useRef<HTMLDivElement | null>(null);
  // A restored rider beyond the party (the forms changed meanwhile): the last one.
  useEffect(() => {
    if (riders.length > 0 && riderIndex > riders.length - 1) setRiderIndex(riders.length - 1);
  }, [riders.length, riderIndex]);
  // Riders opens at the first rider not saved yet: after a reload or the
  // trip to Paystack (the saved riders come back from the server's reader,
  // ticked), and whenever the guest comes to it from Your details. Edit on
  // Review and Back from Booking details go where they say.
  const jumpToUnsaved = useRef(step === "riders");
  const ridersLoaded = !!summary?.loaded;
  const savedKey = riders.map((r) => (r.saved ? "1" : "0")).join("");
  useEffect(() => {
    if (!jumpToUnsaved.current || step !== "riders" || !ridersLoaded) return;
    jumpToUnsaved.current = false;
    const index = resumeRiderIndex(savedKey.split("").map((f) => f === "1"), riderIndex);
    if (index !== riderIndex) setRiderIndex(index);
  }, [step, ridersLoaded, savedKey, riderIndex]);

  // ── Focus and the announcement on every step and card change ──
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [live, setLive] = useState("");
  // The card on screen (its title's id).
  const slot = riders[riderIndex];
  const slotKey = slot ? `${slot.bookingId}-${slot.ordinal}` : null;
  /** The focus is on something in the page (not <body>, not outside the checkout). */
  function focusInPage(): boolean {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body && !!pageRef.current?.contains(active);
  }
  /**
   * A save that did not move the guest on: the focus goes back to `el`
   * (the control pressed: busy, or while the riders' forms read again, it
   * is aria-disabled, so it keeps the focus), or to the words beside the
   * button when that control is off now (null: the hold ended). A guest
   * who has moved on elsewhere in the page stays there. True once the
   * focus is where it belongs.
   */
  function restoreFocus(el: HTMLElement | null): boolean {
    const lost = !focusInPage();
    const off = !el || !el.isConnected || (el as HTMLButtonElement).disabled === true;
    if (!off) return lost ? take(el) : true;
    if (!lost && (document.activeElement as HTMLButtonElement).disabled !== true) return true;
    return take(errorRef.current);
  }
  // Where the focus goes once the view it points at is on screen: a
  // request set in the SAME update as that view (useFocusRequest: never a
  // ref set after an await, which an effect still pending from an earlier
  // render could spend before the target was there).
  const focusTo = useFocusRequest<PageFocus>(
    // On opening: the step's heading, and why a restored checkout reopened
    // ("We haven't received your payment…": a status inserted with its
    // words is often not read).
    { to: "heading", say: resume?.notice ?? null },
    (want) => {
      // Each branch is true only once its target HAS the focus (or nothing
      // needed moving): a target not there yet, or one that would not take
      // the focus, leaves the request waiting (then the fallback).
      if (want.to === "heading") {
        if (!take(headingRef.current)) return false;
        // The heading says its own name as it takes focus; the announcement
        // adds where the guest is ("Step 2 of 4"), not the name again.
        setLive([want.say, stepPositionText(steps, step)].filter(Boolean).join(" "));
        return true;
      }
      if (want.to === "field") {
        const field = document.getElementById(want.id);
        if (!field || !take(field)) return false;
        revealWithMessage(field);
        return true;
      }
      if (want.to === "problem" && step === "booking") {
        if (c.participantHandle.current?.focusProblem("group") && focusInPage()) return true;
        // Nothing missing to go to (the server refused the answers): the
        // control pressed keeps the focus, with the words beside it.
        return want.el ? restoreFocus(want.el) : false;
      }
      if (want.to === "title" || want.to === "problem") {
        if (!slot) return false;
        if (want.to === "problem" && c.participantHandle.current?.focusProblem({ rider: riderIndex }) && focusInPage())
          return true;
        return take(document.getElementById(`participant-${slot.bookingId}-${slot.ordinal}-title`));
      }
      return restoreFocus(want.to === "restore" ? want.el : null);
    },
    // A target that never appeared: the words beside the button, else the
    // step's heading — never the page behind the checkout.
    () => errorRef.current ?? headingRef.current,
    [step, riderIndex, slotKey],
  );
  /** Another step; `say`: said before "Step 3 of 4" ("Rider 4 saved."). */
  function go(next: StepId, rider?: number, say?: string | null) {
    setRiderMessage(null);
    setStepMessage(null);
    setNotice(null);
    setResumeNoteOn(false);
    c.setError(null);
    focusTo({ to: "heading", say: say ?? null });
    if (rider !== undefined) setRiderIndex(rider);
    setStep(next);
  }
  /** Another rider's card; `announce`: said first ("Rider 1 saved."). */
  function showRider(index: number, focus: boolean, announce?: string | null) {
    setRiderMessage(null);
    setStepMessage(null);
    // A chip the guest picked keeps the focus (they are moving along the tabs).
    focusTo(focus ? { to: "title" } : null);
    setRiderIndex(index);
    const title = riders[index]?.title;
    if (title) setLive([announce, `${title} of ${riders.length}`].filter(Boolean).join(" "));
  }
  /** Next found something missing on a rider's card: say so, and go to it (the first question it still needs). */
  function riderMissing(index: number, message: string | null) {
    setRiderMessage(message);
    setStepMessage(message);
    focusTo({ to: "problem" });
    setRiderIndex(index);
  }
  /**
   * A rider's save was not accepted: the words beside the button (an alert)
   * and on that rider's card, the card on screen with its answers as they
   * are (the one rider_changed reloaded shows the server's): the question
   * the server found wanting, another rider's card by its title, else the
   * focus back where it was (`focused`, the control pressed). The hold
   * ended: the words beside the button only, and nothing moves on.
   */
  function riderNotSaved(index: number, problem: RiderSaveProblem, focused: HTMLElement | null) {
    setStepMessage(problem.message);
    if (problem.kind === "lapsed") {
      // Next goes off: the words beside it take the focus it had.
      focusTo({ to: "message" });
      return;
    }
    setRiderMessage(problem.message);
    if (problem.kind === "invalid") focusTo({ to: "problem" });
    else if (index !== riderIndex) focusTo({ to: "title" });
    else if (focused) focusTo({ to: "restore", el: focused });
    setRiderIndex(index);
  }

  // ── Resume: where the guest is, never what they typed ──
  const holdEnded = c.holdEnded;
  // Whether a code is applied, and of which kind (never the code): a
  // restored Review asks for it again.
  const codesApplied = c.appliedCodes.length > 0;
  const promoApplied = c.appliedCodes.some((code) => code.kind !== "gift_card");
  const giftCardApplied = c.appliedCodes.some((code) => code.kind === "gift_card");
  const linkCodeOnly = c.onlyLinkCode;
  // Yes/no only: whether Special requests or a Country other than the default were given.
  const notesGiven = c.notes.trim() !== "";
  const countryGiven = c.hasAccommodation && c.country !== "ZA";
  const marketing = c.consentMarketing;
  useEffect(() => {
    if (holdEnded) {
      clearCheckoutResume(tenant.slug);
      return;
    }
    saveCheckoutResume(tenant.slug, {
      cartToken: getCartToken(),
      items,
      step,
      riderIndex,
      payment: paymentRef.current,
      codesApplied,
      codeKinds: [...(promoApplied ? (["promo"] as const) : []), ...(giftCardApplied ? (["gift_card"] as const) : [])],
      linkCodeOnly,
      marketing,
      given: { notes: notesGiven, country: countryGiven },
    });
  }, [tenant.slug, items, step, riderIndex, holdEnded, codesApplied, promoApplied, giftCardApplied, linkCodeOnly, marketing, notesGiven, countryGiven]);

  // ── A dialog that keeps the keyboard in it (aria-modal) ──
  // Tab past the last control comes back to the first, and Shift+Tab before
  // the first goes to the last; a Tab from outside (focus lost to the page
  // behind, as when a control on screen went away) comes back in. Escape
  // does not close the page: that would drop every answer typed so far; the
  // × button closes it.
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const controls = tabbable(root);
      if (controls.length === 0) return;
      const first = controls[0]!;
      const last = controls[controls.length - 1]!;
      const active = document.activeElement;
      if (!(active instanceof Node) || !root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  // ── The page never drops the focus to the page behind by itself ──
  // A control that had the focus and that the page took away — drawn again
  // in the other layout (a phone turned, a window resized across the
  // phone/wide switch: Next, Back and the words move between the action
  // bar and the summary column), or turned off (a card's chips while its
  // forms are read again) — hands the focus to its twin in the new layout
  // (data-focus-key), else the words beside the button, else the step's
  // heading. A guest who put the focus nowhere (a tap on blank space) is
  // left so, and a save or the payment on its way places the focus itself
  // once it answers (the focus requests). A press on a control that is off
  // (Pay before its ticks; the second tap of a double tap landing on it)
  // never takes the focus from where it is (the browser moved it to the
  // page as the pointer went down), and the second tap of a double tap on
  // Next or Back neither acts (oneTap) nor takes the focus.
  const placesFocus = useRef(false);
  placesFocus.current = busy || c.pending;
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const onOut = (e: FocusEvent) => {
      const from = e.target;
      if (e.relatedTarget || !(from instanceof HTMLElement)) return;
      // After the change that took it (a removal blurs it while it is still in the page).
      queueMicrotask(() => {
        const active = document.activeElement;
        if (active && active !== document.body) return;
        if (from.isConnected && !from.matches(":disabled")) return;
        if (placesFocus.current) return;
        const key = from.dataset.focusKey;
        const twin = key ? root.querySelector<HTMLElement>(`[data-focus-key="${key}"]`) : null;
        for (const el of [twin, errorRef.current, headingRef.current]) {
          if (el && !el.matches(":disabled") && take(el)) return;
        }
      });
    };
    // (A disabled button gets no mousedown, only the pointer's.)
    const onPointerDown = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest("button:disabled")) e.preventDefault();
    };
    // The second tap of a double tap on Next or Back does nothing at all
    // (oneTap): not even take the focus from the heading the first one
    // moved it to.
    const onMouseDown = (e: MouseEvent) => {
      if (e.detail > 1 && e.target instanceof Element && e.target.closest('[data-focus-key="primary"], [data-focus-key="back"]'))
        e.preventDefault();
    };
    root.addEventListener("focusout", onOut);
    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("mousedown", onMouseDown);
    return () => {
      root.removeEventListener("focusout", onOut);
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("mousedown", onMouseDown);
    };
  }, []);

  // The summary column (wider screens) is as tall as the scroller at most,
  // its details scrolling inside it, so the primary button under them is
  // always on screen, however many riders it lists and at any text size.
  useEffect(() => {
    const page = pageRef.current;
    const scroller = c.checkoutFormRef.current;
    if (!page || !scroller || typeof ResizeObserver === "undefined") return;
    const measure = () => page.style.setProperty("--vt-co-scroller-h", `${scroller.clientHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── The primary action of each step ──
  const handle = () => c.participantHandle.current;
  // Every rider already in (a restored checkout, or Back to Your details
  // after the riders): Continue on Your details goes past Riders, which has
  // nothing left to do. Riders is still one Back away, and Edit on Review.
  const ridersAllIn = !!summary?.loaded && nothingLeftToSave(riders.map((r) => r.saved));
  function continueTo(from: StepId): StepId | null {
    const next = nextStepOf(steps, from);
    return from === "details" && next === "riders" && ridersAllIn ? nextStepOf(steps, "riders") : next;
  }
  const lapsed = c.paymentLapsed;
  /**
   * The guest is leaving the rider on screen (Next, Back, another rider's
   * chip): save it first (the one-rider writer), unless it is saved as
   * shown already. The rules are the drawer pager's too (leaveBeforeSave,
   * leaveAfterSave): Next needs the rider complete (what is missing is
   * said, nothing is sent); Back and a chip leave a partly filled rider as
   * it is (never saved, never ticked). A refusal keeps the guest on the
   * card and says why; the network alone lets Back or a chip move on (the
   * answers stay in the page's memory, unticked, and the end of the step
   * tries again), while Next stays and tries again on the next Next.
   */
  async function leaveRider(how: "next" | "move"): Promise<{ go: boolean; announce?: string | null; after?: string | null }> {
    const h = handle();
    const ready = !!h && !!summary?.loaded;
    // The hold ended: nothing can be saved any more; Back and the chips still move.
    const before = leaveBeforeSave(how, {
      holdOver: !ready || holdEnded || lapsed,
      issue: ready ? h.riderIssue(riderIndex) : null,
    });
    if (before) return settleLeave(before, null);
    // The button that tries again: "Next rider", or "Continue to …" on the last.
    const again = retryButton("page", riderIndex >= riders.length - 1);
    const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setBusy(true);
    const result = await h!.saveRider(riderIndex, { again });
    setBusy(false);
    return settleLeave(leaveAfterSave(result, how, again), focused);
  }
  /** What leaveRider's outcome shows: what is missing, or why the rider was not saved (focus back where it was). */
  function settleLeave(out: LeaveOutcome, focused: HTMLElement | null): { go: boolean; announce?: string | null; after?: string | null } {
    if (out.go) return out;
    const problem = out.problem;
    if (!problem) return { go: false };
    if (problem.kind === "incomplete") {
      riderMissing(riderIndex, problem.message);
      return { go: false };
    }
    riderNotSaved(riderIndex, problem, focused);
    return { go: false };
  }
  async function primary() {
    if (busy || holdEnded || lapsed) return;
    setMoves((n) => n + 1);
    c.setError(null);
    setStepMessage(null);
    setNotice(null);
    if (step === "details") {
      const form = c.checkoutFormRef.current;
      if (form && !form.reportValidity()) return;
      const check = normalizePhoneE164(c.phone);
      if (!check.ok) {
        // The field, once the words under it are drawn (the same update):
        // focused before them, the words landed under the action bar.
        c.setPhoneError(check.error ?? null);
        focusTo({ to: "field", id: `${c.contactId}-phone` });
        return;
      }
      c.setPhoneError(null);
      if (check.e164 && check.e164 !== c.phone) c.setPhone(check.e164);
      const next = continueTo(step);
      if (next === "riders") jumpToUnsaved.current = true;
      if (next) go(next);
      // Riders went by (every rider is in already): say so where it went.
      if (next && next !== nextStepOf(steps, step)) setNotice(ridersSkippedNotice(noun));
      return;
    }
    if (step === "riders") {
      const h = handle();
      if (!h || !summary?.loaded) return;
      // This rider is saved now (or already was), or the guest stays on it.
      const left = await leaveRider("next");
      if (!left.go) return;
      if (riderIndex < riders.length - 1) {
        showRider(riderIndex + 1, true, left.announce);
        return;
      }
      const missing = h.firstIncompleteRider();
      if (missing >= 0) {
        riderMissing(missing, h.riderIssue(missing));
        return;
      }
      // Every rider is complete: any not saved yet (a save the network lost
      // on Back or a chip) goes now, one at a time — never the whole list.
      const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setBusy(true);
      const rest = await h.saveRiders("Continue");
      setBusy(false);
      if (rest) {
        riderNotSaved(rest.index, rest.problem, focused);
        return;
      }
      const next = nextStepOf(steps, step);
      if (next) go(next, undefined, left.announce);
      return;
    }
    if (step === "booking") {
      const h = handle();
      if (!h) return;
      const focused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setBusy(true);
      const problem = await h.save("group");
      setBusy(false);
      if (problem) {
        // The question, once the words beside the button are drawn (the
        // same update): focused before them, the words grew the action bar
        // over it.
        setStepMessage(problem);
        focusTo({ to: "problem", el: focused });
        return;
      }
      const next = nextStepOf(steps, step);
      if (next) go(next);
    }
  }
  async function back() {
    if (busy) return;
    setMoves((n) => n + 1);
    if (step === "riders") {
      const prev = prevStepOf(steps, step);
      // Back from Rider 1 to Your details while "Yes, I'm riding" is on:
      // not saved (Rider 1 holds the booker's own details, and the guest may
      // be going back to say No; a saved Rider 1 would take the question
      // away). It is saved on the next Next, like any rider.
      const toDetails = riderIndex === 0 && prev === "details";
      const left = toDetails && handle()?.prefillOn(0) ? { go: true } : await leaveRider("move");
      if (!left.go) return;
      if (riderIndex > 0) {
        showRider(riderIndex - 1, true, left.announce);
        if (left.after) setStepMessage(left.after);
        return;
      }
      if (!prev) {
        leave(onBack)();
        return;
      }
      go(prev, undefined, left.announce);
      if (left.after) setStepMessage(left.after);
      return;
    }
    const prev = prevStepOf(steps, step);
    if (!prev) {
      leave(onBack)();
      return;
    }
    go(prev, prev === "riders" ? Math.max(0, riders.length - 1) : undefined);
  }
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    if (step !== "review") {
      // Before Review the form has no submit button, so the browser submits
      // it only from a step with a single text field (Enter there moves the
      // guest on, as Next would); Enter on the focused Next is the way on
      // everywhere. It never pays.
      e.preventDefault();
      void primary();
      return;
    }
    c.submit(e);
  }

  // ── What the forms show on this step ──
  const lineInfo = (bookingId: string) => {
    const item = items.find((i) => i.metadata?.booking_id === bookingId);
    return item ? { title: lineTitle(item), when: lineWhen(item) } : null;
  };
  /** Another rider's chip: the rider on screen is saved first (leaveRider), then the chip's rider shows. */
  async function pickRider(index: number) {
    if (busy || index === riderIndex) return;
    setMoves((n) => n + 1);
    const left = await leaveRider("move");
    if (!left.go) return;
    showRider(index, false, left.announce);
    if (left.after) setStepMessage(left.after);
  }
  const stepped: SteppedForms = {
    part: step === "details" ? "prefill" : step === "riders" ? "riders" : step === "booking" ? "booking" : "review",
    riderIndex,
    onRiderIndex: (index) => void pickRider(index),
    onLapse: c.markLapsed,
    compact: phone,
    lineInfo,
    onEdit: (target) => (target.step === "riders" ? go("riders", target.riderIndex) : go("booking")),
    riderMessage,
    // The guest answered what the card asked ("Is this still {name}?",
    // "Who is not coming?", P4a): the words that asked it go.
    onAnswered: () => {
      setRiderMessage(null);
      setStepMessage(null);
    },
  };

  // The reopened checkout's notice, without "apply your code again" once the
  // codes are back on (a campaign link's code is applied again by itself).
  const shownNotice = noticeNow(notice, resume ?? null, c.appliedCodes, c.linkCode);

  // ── The summary ──
  const dueCents = c.isMixedCart ? c.mixedDueNowCents : c.vertuNetCents;
  const due = formatDue(currency, dueCents);
  const discount = summaryDiscount({
    currency,
    totalCents: c.totalCents,
    discountCents: c.discountCents,
    codes: c.appliedCodes,
    mixedCart: c.isMixedCart,
  });
  const lines: SummaryLine[] = items.map((item) => ({
    key: item.id,
    title: lineTitle(item),
    when: lineWhen(item),
    imageUrl: welcome && appearance.show_summary_images ? summaryImages?.[item.id] : undefined,
    party: isChideskItem(item) ? null : partyText(item, forms.find((f) => f.booking_id === item.metadata?.booking_id)?.guest_noun ?? null),
  }));
  const summaryRiders: SummaryRider[] = riders.map((r, index) => ({
    key: r.key,
    title: r.title,
    name: r.name,
    saved: r.saved,
    current: step === "riders" && index === riderIndex,
  }));
  const firstWhen = items.map(lineWhen).find(Boolean) ?? null;

  const current = steps.find((s) => s.id === step)!;
  // The page covers the widget's header: its top bar carries the same venue
  // (the active brand's, else the tenant's; the tenant alone outside the shell).
  const venue = useVenueIdentityOr(tenant);
  const formId = useId();
  const isReview = step === "review";
  // A guard notice (GuardNotice, on Review) replaces the resume notice ("We
  // haven't received your payment") rather than sitting beside it. "Went
  // through" turns Pay off and says "Payment received" (payBlocked).
  useEffect(() => {
    if (c.guard) setNotice(null);
  }, [c.guard]);
  const lastRider = riderIndex >= riders.length - 1;
  const primaryLabel = isReview
    ? c.submitLabel
    : busy
      ? "Saving…"
      : step === "riders" && !lastRider
        ? `Next ${noun.one}`
        : `Continue to ${steps.find((s) => s.id === continueTo(step))?.label ?? "the next step"}`;
  // Busy (a save on its way), and Riders while its forms are read (again,
  // after a refusal that reloads them), is aria-disabled, not disabled: the
  // button keeps its focus, so a save that does not move the guest on
  // leaves them where they were (primary() and back() do nothing then). A
  // disabled Next blurred to the page as the forms reloaded.
  const primaryDisabled = isReview ? payBlocked(c) : holdEnded || lapsed;
  const primaryBusy = !isReview && (busy || (step === "riders" && !summary?.loaded));
  const primaryButton = (
    <button
      type={isReview ? "submit" : "button"}
      form={formId}
      className="vt-cta vt-checkout-page-primary"
      data-focus-key="primary"
      disabled={primaryDisabled}
      aria-disabled={primaryBusy && !primaryDisabled ? true : undefined}
      onClick={isReview ? undefined : oneTap(primary)}
    >
      {primaryLabel}
    </button>
  );
  const backLabel =
    step === "riders" && riderIndex > 0
      ? `← ${riders[riderIndex - 1]?.title ?? `${nounTitle(noun)} ${riderIndex}`}`
      : prevStepOf(steps, step)
        ? "← Back"
        : "← Back to your visit";
  const backButton = (
    <button
      type="button"
      className="vt-checkout-page-back"
      data-hold-passive
      data-focus-key="back"
      onClick={oneTap(back)}
      aria-disabled={busy ? true : undefined}
    >
      {backLabel}
    </button>
  );
  // The hold ended by a save's reply (lapsed): the words stay for as long as
  // Next is off, whatever the guest taps or types (the hold timer's own
  // banner says it once its clock runs out: holdEnded).
  const message = stepMessage ?? c.error ?? (lapsed && !holdEnded ? LAPSE_MESSAGE : null);
  const errorBox = message ? (
    <div ref={errorRef} tabIndex={-1} className="vt-capacity-error vt-checkout-page-error" role="alert" data-focus-key="words">
      {message}
    </div>
  ) : null;
  const timer = <HoldTimer items={items} formRef={pageRef} onBack={leave(onBack)} onEndedChange={c.setHoldEnded} />;

  return (
    <div
      ref={pageRef}
      className="vt-checkout-page"
      role="dialog"
      aria-modal="true"
      aria-label={`Checkout — ${venue.title}`}
      data-phone={phone ? "true" : "false"}
      data-step={step}
      data-presentation={appearance.layout}
    >
      <div className="vt-checkout-page-top">
        {/* The widget header's look (.vt-header), with "Checkout" beside the ×. */}
        <div className="vt-header vt-checkout-page-bar">
          <VenueMark identity={venue} titleAs="div" decorative taglineOneLineOrNone={phone} />
          <div className="vt-checkout-page-bar-end">
            <h2 className="vt-checkout-page-title">Checkout</h2>
            <button type="button" className="vt-cart-close" data-hold-passive onClick={leave(onClose)} aria-label="Close">
              ×
            </button>
          </div>
        </div>
        {phone && (
          <SummaryBar
            lines={lines}
            riders={summaryRiders}
            noun={noun}
            dueLabel="Due now"
            due={due}
            discount={discount}
            when={firstWhen}
            // Folded away again on every step and rider change, and on
            // every Next or Back: open, it takes the room the card needs.
            closeKey={`${step}:${riderIndex}:${moves}`}
          />
        )}
        {phone && <div className="vt-checkout-page-timer">{timer}</div>}
      </div>

      <form
        id={formId}
        ref={c.checkoutFormRef}
        className="vt-cart-checkout vt-checkout-page-form"
        onSubmit={onSubmit}
        // What Next found missing goes once the guest starts putting it right.
        onChange={() => {
          if (stepMessage) setStepMessage(null);
          if (riderMessage) setRiderMessage(null);
        }}
      >
        {welcome && (
          <div className="vt-checkout-welcome">
            <CheckoutProgress steps={steps} current={step} compact={phone} numbered />
            {step === "details" && (appearance.heading || appearance.description) && (
              <div className="vt-checkout-welcome-intro">
                {appearance.heading && <h2 className="vt-checkout-welcome-title">{appearance.heading}</h2>}
                {appearance.description && <p className="vt-checkout-welcome-description">{appearance.description}</p>}
              </div>
            )}
          </div>
        )}
        <div className="vt-checkout-page-body">
          <div className="vt-checkout-page-main">
            {!welcome && <CheckoutProgress steps={steps} current={step} compact={phone} />}
            <p className="vt-hold-sr" role="status" aria-live="polite" aria-atomic="true">
              {live}
            </p>
            <h3 ref={headingRef} tabIndex={-1} className="vt-checkout-step-title">
              {current.label}
            </h3>
            {shownNotice && (
              <p className="vt-checkout-page-notice" role="status">
                {shownNotice}
              </p>
            )}
            {step === "details" && resumeNoteOn && (() => {
              const said = resumeSavedNotice(noun, riders.filter((r) => r.saved).length, riders.length);
              return said ? (
                <p className="vt-checkout-page-notice vt-checkout-resume-note" role="status">
                  {said}
                </p>
              ) : null;
            })()}

            {step === "details" && c.isMixedCart && (
              <p className="vt-checkout-note">
                You&apos;re booking spa treatments alongside your other items — we&apos;ll take one payment now
                (your bookings in full plus the spa deposit); the spa treatment balance is settled at the venue.
              </p>
            )}
            {step === "details" && <ContactFields c={c} />}

            {/* Always at this place in the tree, whatever the step: the
                forms keep what the guest typed in memory across the steps. */}
            {c.participantSignature && (
              <ParticipantDetails
                cartToken={getCartToken()}
                signature={c.participantSignature}
                onValidityChange={c.setParticipantsValid}
                handleRef={c.participantHandle}
                booker={{ firstName: c.firstName, lastName: c.lastName, email: c.email, phone: c.phone }}
                venueName={tenant.name}
                stepped={stepped}
                onSummary={setSummary}
              />
            )}

            {((step === "details" && !hasBookingStep) || step === "booking") && <SpecialRequestsField c={c} />}

            {isReview && (
              <>
                <CheckoutTotals c={c} currency={currency} />
                <CheckoutConsents c={c} />
                <CohostNotice c={c} />
                <GuardNotice c={c} onChooseAnotherTime={leave(onChooseAnotherTime)} />
                {phone && <PayNotes c={c} />}
              </>
            )}
            {welcome && !phone && (
              <div className="vt-checkout-form-actions">
                {errorBox}
                {primaryButton}
                <div className="vt-checkout-page-nav">{backButton}</div>
                {isReview && <PayNotes c={c} />}
              </div>
            )}
          </div>

          {!phone && (
            // Classic keeps its actions beside the form. Welcome puts its
            // single primary action immediately after the fields instead.
            <SummaryAside lines={lines} riders={summaryRiders} noun={noun} dueLabel="Due now" due={due} discount={discount}>
              {timer}
              {!welcome && (
                <>
                  {errorBox}
                  {primaryButton}
                  <div className="vt-checkout-page-nav">{backButton}</div>
                  {isReview && <PayNotes c={c} />}
                </>
              )}
            </SummaryAside>
          )}
        </div>
      </form>

      {phone && (
        <div className="vt-checkout-actionbar">
          {errorBox}
          <div className="vt-checkout-actionbar-row">
            {backButton}
            {primaryButton}
          </div>
        </div>
      )}
    </div>
  );
}
